import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";
import EmployeeLayout from "./EmployeeLayout";
import { toast } from "../utils/toast";
import { formatKolkataDateTime } from "../utils/dateTime";

const allowedMimeTypes = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/webp",
]);

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ResumeUpload() {
  const inputRef = useRef(null);
  const loadingExistingRef = useRef(false);
  const pollDelayRef = useRef(10000);
  const user = getStoredUser();
  const isEmployee = user?.user_type === "employee" || user?.role === "employee";
  const canUpload = !isEmployee || user?.can_upload_resumes !== false;
  const Layout = isEmployee ? EmployeeLayout : CompanyLayout;
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [existingResumes, setExistingResumes] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const companyLabel = useMemo(() => {
    return user?.company_name || user?.first_name || "your company";
  }, [user]);

  const validateFiles = (files) => {
    for (const file of files) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const allowedExt = ["pdf", "doc", "docx", "png", "jpg", "jpeg", "webp"].includes(ext);
      if (!allowedMimeTypes.has(file.type) && !allowedExt) {
        throw new Error(`${file.name} is not supported. Upload PDF, DOC, DOCX, PNG, JPG, JPEG, or WEBP.`);
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`${file.name} is larger than 5MB.`);
      }
    }
  };

  const loadExistingResumes = useCallback(async () => {
    if (loadingExistingRef.current) return;
    if (!canUpload) {
      setExistingResumes([]);
      return;
    }

    loadingExistingRef.current = true;
    setLoadingExisting(true);
    try {
      const response = await fetch(`${ApiURL}get_visible_resumes.php`, {
        headers: getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body.success === false) {
        throw new Error(body.message || "Failed to load uploaded resumes");
      }
      setExistingResumes(Array.isArray(body.data) ? body.data : []);
    } catch (error) {
      console.error("Error loading company resumes:", error);
      toast.error(error.message || "Failed to load uploaded resumes");
      setExistingResumes([]);
    } finally {
      loadingExistingRef.current = false;
      setLoadingExisting(false);
    }
  }, [canUpload]);

  useEffect(() => {
    loadExistingResumes();
  }, [loadExistingResumes]);

  // Refresh sequentially with backoff; a slow parse must not hammer the library API.
  const hasPendingParsing = existingResumes.some((resume) =>
    ["queued", "pending", "processing"].includes(String(resume.processing_status || "").toLowerCase())
  );
  useEffect(() => {
    if (!hasPendingParsing) {
      pollDelayRef.current = 10000;
      return undefined;
    }

    let cancelled = false;
    let timer;
    const poll = async () => {
      if (!document.hidden) await loadExistingResumes();
      if (cancelled) return;
      pollDelayRef.current = Math.min(pollDelayRef.current * 2, 60000);
      timer = window.setTimeout(poll, pollDelayRef.current);
    };
    timer = window.setTimeout(poll, pollDelayRef.current);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hasPendingParsing, loadExistingResumes]);

  const uploadOne = async (file, attempt = 1) => {
    const formData = new FormData();
    formData.append("resume", file);

    try {
      const response = await fetch(`${ApiURL}upload_resume.php`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });

      const text = await response.text();
      const body = text ? JSON.parse(text) : {};

      if (response.status === 409 || body.code === "duplicate_resume") {
        return { file: file.name, status: "duplicate", message: body.message || "Duplicate candidate skipped" };
      }

      if (!response.ok || body.success === false) {
        if (attempt < 2 && response.status >= 500) {
          return uploadOne(file, attempt + 1);
        }
        return { file: file.name, status: "failed", message: body.message || `Upload failed (${response.status})` };
      }

      const processingStatus = body?.data?.processing_status || "uploaded";
      return {
        file: file.name,
        status: processingStatus === "failed"
          ? "extraction_failed"
          : ["queued", "pending", "processing"].includes(processingStatus) ? "parsing" : "uploaded",
        message: body.message || (processingStatus === "queued" ? "Uploaded; parsing queued" : "Uploaded"),
      };
    } catch (error) {
      if (attempt < 2) {
        return uploadOne(file, attempt + 1);
      }
      return { file: file.name, status: "failed", message: error.message || "Upload failed" };
    }
  };

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    if (!canUpload) {
      toast.error("You do not have permission to upload resumes.");
      event.target.value = "";
      return;
    }

    try {
      validateFiles(files);
    } catch (error) {
      toast.error(error.message);
      event.target.value = "";
      return;
    }

    setUploading(true);
    setResults([]);
    const nextResults = [];

    try {
      const uploadBatchSize = 1;
      for (let index = 0; index < files.length; index += uploadBatchSize) {
        const batch = files.slice(index, index + uploadBatchSize);
        const batchResults = await Promise.all(batch.map((file) => uploadOne(file)));
        nextResults.push(...batchResults);
        setResults([...nextResults]);
      }

      const uploaded = nextResults.filter((item) => item.status === "uploaded" || item.status === "parsing").length;
      const duplicates = nextResults.filter((item) => item.status === "duplicate").length;
      const extractionFailed = nextResults.filter((item) => item.status === "extraction_failed").length;
      if (uploaded > 0) toast.success(`${uploaded} resume(s) uploaded. Resume data extraction will continue automatically.`);
      if (duplicates > 0) toast.warning(`${duplicates} duplicate resume(s) skipped.`);
      if (extractionFailed > 0) toast.warning("Some resumes were saved, but automatic parsing could not start. Please use Re-extract.");
      if (uploaded === 0 && duplicates === 0 && extractionFailed === 0) toast.error("No resumes uploaded.");
      if (uploaded > 0 || extractionFailed > 0) {
        await loadExistingResumes();
      }
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <Layout
      title="Upload Resumes"
      description={`Upload candidate resumes directly into ${companyLabel}'s private company pool.`}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Company Resume Pool</h3>
              <p className="mt-1 text-sm text-slate-500">
                Files upload in small batches and duplicate candidates are skipped automatically.
              </p>
            </div>
            <button
              type="button"
              disabled={!canUpload || uploading}
              onClick={() => inputRef.current?.click()}
              className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploading ? "Uploading..." : "Choose Resumes"}
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={handleFiles}
          />
          {!canUpload ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your company admin has not enabled resume upload permission for this recruiter.
            </div>
          ) : null}
        </div>

        {results.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Upload Results</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {results.map((item) => (
                <div key={`${item.file}-${item.status}`} className="flex items-start justify-between gap-4 px-5 py-3 text-sm">
                  <div>
                    <div className="font-medium text-slate-900">{item.file}</div>
                    <div className="mt-1 text-slate-500">{item.message}</div>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      item.status === "uploaded"
                        ? "bg-emerald-100 text-emerald-700"
                        : item.status === "parsing"
                          ? "bg-blue-100 text-blue-700"
                        : item.status === "duplicate"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    {item.status === "extraction_failed" ? "Parsing failed" : item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {canUpload ? (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Already Uploaded Resumes</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {loadingExisting
                    ? "Loading company resume pool..."
                    : `${existingResumes.length} resume(s) available in ${companyLabel}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={loadExistingResumes}
                disabled={loadingExisting}
                className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">File</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Candidate</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Parsing</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Uploaded</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Uploaded By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {existingResumes.map((resume) => (
                    <tr key={resume.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 align-top font-medium text-slate-900">
                        <div>{resume.original_filename || "-"}</div>
                        <div className="mt-1 text-xs text-slate-500">#{resume.id}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        <div>{resume.extracted_name || resume.extracted_data?.full_name || "-"}</div>
                        <div className="mt-1 text-xs text-slate-400">{resume.extracted_email || resume.email || ""}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            resume.processing_status === "success"
                              ? "bg-emerald-100 text-emerald-700"
                              : resume.processing_status === "failed"
                                ? "bg-rose-100 text-rose-700"
                                : resume.processing_status === "duplicate"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {resume.processing_status || "queued"}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        {resume.extracted_category || resume.category || "-"}
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        {formatKolkataDateTime(resume.upload_date)}
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        <div className="font-medium text-slate-900">{resume.uploaded_by_name || "-"}</div>
                        <div className="text-xs text-slate-500">{resume.uploaded_by_display_id || "-"}</div>
                      </td>
                    </tr>
                  ))}
                  {!loadingExisting && existingResumes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                        No resumes have been uploaded to this company pool yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
